import * as React from 'react';
import * as classnames from 'classnames';
import styled, { StyledComponentBase } from 'styled-components';
import { breakpoint, ContentSpacing } from '@estee/elc-base-theme';
import { tabHeaderTheme } from '../theme/default-theme';

export interface ITabHeader {
    className?: string;
    icon?: React.ReactNode;
    isCurrent: boolean;
    onSelect(currentTabId: string): void;
    onSelectNext(currentTabId: string): void;
    onSelectPrevious(currentTabId: string): void;
    tabHeaderId: string;
    tabId: string;
    tabPanelId: string;
    title: string;
    subtitle?: string;
    keepButtonTabOrder?: boolean;
}

const StyledTabHeader: StyledComponentBase<'button', ITabHeader> = styled.button`
    border-style: solid;
    border-width: 1px;
    margin: 0 0 ${ContentSpacing.space12};
    padding: ${ContentSpacing.space16};
    width: 100%;
    ${tabHeaderTheme};

    ${breakpoint('desktop')`
        margin: 0 ${ContentSpacing.space24} ${ContentSpacing.space24} 0;
        padding: ${ContentSpacing.space28};

        &:last-child {
            margin-right: 0;
        }

        [dir='rtl'] & {
            margin-left  ${ContentSpacing.space24};
            margin-right  0;

            &:last-child {
                margin-left: 0;
            }
        }
    `};

    .elc-icon {
        margin: 0 auto ${ContentSpacing.space10};
    }
`;

StyledTabHeader.displayName = 'StyledTabHeader';

export class TabHeader extends React.Component<ITabHeader> {
    private tabHeaderRef: React.RefObject<HTMLButtonElement> = React.createRef();

    componentDidUpdate(prevProps: ITabHeader) {
        if (
            this.tabHeaderRef.current &&
            this.props.isCurrent &&
            prevProps.isCurrent !== this.props.isCurrent
        ) {
            this.tabHeaderRef.current.focus();
        }
    }

    private onClick = () => {
        this.props.onSelect(this.props.tabId);
    };

    private onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (event.key === 'Right' || event.key === 'ArrowRight') {
            this.props.onSelectNext(this.props.tabId);
        }

        if (event.key === 'Left' || event.key === 'ArrowLeft') {
            this.props.onSelectPrevious(this.props.tabId);
        }
    };

    private get tabIndex(): number | undefined {
        const { isCurrent, keepButtonTabOrder } = this.props;
        switch (true) {
            case keepButtonTabOrder:
                return undefined;
                break;
            case isCurrent && !keepButtonTabOrder:
                return 0;
                break;
            case !isCurrent && !keepButtonTabOrder:
                return -1;
                break;
            default:
                return 0;
        }
    }

    public render() {
        const { icon, isCurrent, tabHeaderId, tabPanelId, title, subtitle, className } = this.props;
        const classNames = classnames('elc-tabs__tab', 'js-elc-tabs__tab', className, {
            'elc-tabs__tab--current': isCurrent
        });

        return (
            <StyledTabHeader
                aria-controls={tabPanelId}
                aria-selected={isCurrent}
                className={classNames}
                id={tabHeaderId}
                ref={this.tabHeaderRef}
                onClick={this.onClick}
                onKeyDown={this.onKeyDown}
                role={'tab'}
                tabIndex={this.tabIndex}
            >
                {icon && <div className="elc-tabs__tab-icon js-elc-tabs__tab-icon">{icon}</div>}
                <div className="elc-tabs__tab-text js-elc-tabs__tab-text">{title}</div>
                <div className="elc-tabs__tab-subtitle js-elc-tabs__tab-subtitle">{subtitle}</div>
            </StyledTabHeader>
        );
    }
}
